const STORAGE_KEYS = {
  ACCESS_TOKEN: 'ballm_access_token',
  REFRESH_TOKEN: 'ballm_refresh_token',
  USER: 'ballm_user',
  CART: 'ballm_cart',
  FCM_TOKEN: 'ballm_fcm_token',
} as const;

// Check if localStorage is available (Safari private mode, etc.)
const isLocalStorageAvailable = (): boolean => {
  try {
    const testKey = '__storage_test__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

// Fallback in-memory storage for when localStorage is unavailable
const memoryStorage: Record<string, string> = {};

const safeGetItem = (key: string): string | null => {
  try {
    if (isLocalStorageAvailable()) {
      return localStorage.getItem(key);
    }
    return memoryStorage[key] || null;
  } catch {
    return memoryStorage[key] || null;
  }
};

const safeSetItem = (key: string, value: string): void => {
  try {
    if (isLocalStorageAvailable()) {
      localStorage.setItem(key, value);
    }
    memoryStorage[key] = value;
  } catch {
    memoryStorage[key] = value;
  }
};

const safeRemoveItem = (key: string): void => {
  try {
    if (isLocalStorageAvailable()) {
      localStorage.removeItem(key);
    }
    delete memoryStorage[key];
  } catch {
    delete memoryStorage[key];
  }
};

export const storage = {
  // Auth tokens
  getAccessToken: (): string | null => {
    return safeGetItem(STORAGE_KEYS.ACCESS_TOKEN);
  },

  setAccessToken: (token: string): void => {
    safeSetItem(STORAGE_KEYS.ACCESS_TOKEN, token);
  },

  getRefreshToken: (): string | null => {
    return safeGetItem(STORAGE_KEYS.REFRESH_TOKEN);
  },

  setRefreshToken: (token: string): void => {
    safeSetItem(STORAGE_KEYS.REFRESH_TOKEN, token);
  },

  clearTokens: (): void => {
    safeRemoveItem(STORAGE_KEYS.ACCESS_TOKEN);
    safeRemoveItem(STORAGE_KEYS.REFRESH_TOKEN);
  },

  // User
  getUser: <T>(): T | null => {
    const user = safeGetItem(STORAGE_KEYS.USER);
    if (user) {
      try {
        return JSON.parse(user);
      } catch {
        return null;
      }
    }
    return null;
  },

  setUser: <T>(user: T): void => {
    safeSetItem(STORAGE_KEYS.USER, JSON.stringify(user));
  },

  clearUser: (): void => {
    safeRemoveItem(STORAGE_KEYS.USER);
  },

  // Cart (persisted)
  getCart: <T>(): T | null => {
    const cart = safeGetItem(STORAGE_KEYS.CART);
    if (cart) {
      try {
        return JSON.parse(cart);
      } catch {
        return null;
      }
    }
    return null;
  },

  setCart: <T>(cart: T): void => {
    safeSetItem(STORAGE_KEYS.CART, JSON.stringify(cart));
  },

  clearCart: (): void => {
    safeRemoveItem(STORAGE_KEYS.CART);
  },

  // FCM Token
  getFcmToken: (): string | null => {
    return safeGetItem(STORAGE_KEYS.FCM_TOKEN);
  },

  setFcmToken: (token: string): void => {
    safeSetItem(STORAGE_KEYS.FCM_TOKEN, token);
  },

  // Clear all
  clearAll: (): void => {
    Object.values(STORAGE_KEYS).forEach(key => {
      safeRemoveItem(key);
    });
  },

  // Check if storage is available
  isAvailable: isLocalStorageAvailable,
};
